import React from "react";
import { ColumnConfig } from "../../../../common/tables/config/ColumnConfig";
import { EnumResolver } from "../../../../common/tables/etc/EnumResolver";
import { TableCellChangedHandler, TableCellFactory } from "../../../../common/tables/factory";
import { VariantTableRow } from "../../../../../../common/tables/product-listing/variants/VariantTableRow";
import { VariantEnumMapper } from "../etc/VariantEnumMapper";
import { VariantWithCostMetadataCount } from "../../../../datastore/ShopifyProductDataStore";

export interface VariantTableCellCallbacks {
    onCostMetadataClick?: (rowData: VariantWithCostMetadataCount) => void;
}

export class VariantTableCellFactory extends TableCellFactory<VariantTableRow, VariantWithCostMetadataCount> {
    readonly enumResolver: EnumResolver<VariantTableRow> = new VariantEnumMapper();
    private callbacks: VariantTableCellCallbacks;

    constructor(callbacks: VariantTableCellCallbacks = {}) {
        super();
        this.callbacks = callbacks;
    }

    public getRowClassName(rowData: VariantWithCostMetadataCount): string {
        return (rowData.costMetadata?.length ?? 0) === 0 ? "jc-row-warning" : "";
    }

    public renderBodyCell(
        columnConfig: ColumnConfig<VariantTableRow>,
        rowData: VariantWithCostMetadataCount,
        onCellChanged: TableCellChangedHandler<VariantTableRow>,
    ): JSX.Element {
        if (columnConfig.columnName === "costMetadataCount") {
            const count = rowData.costMetadata?.length ?? 0;
            const text = count > 0 ? `${count} assigned` : "None";
            if (count > 0) {
                return React.createElement(
                    "span",
                    { className: "jc-cell-clickable", onClick: () => this.callbacks.onCostMetadataClick?.(rowData) },
                    text,
                );
            }
            return React.createElement(
                "span",
                { className: "jc-cell-actionable", onClick: () => this.callbacks.onCostMetadataClick?.(rowData) },
                React.createElement("span", { className: "jc-cell-hover-text" }, "Click to assign"),
            );
        }

        return super.renderBodyCell(columnConfig, rowData, onCellChanged);
    }

    protected resolveCellData(
        columnConfig: ColumnConfig<VariantTableRow>,
        rowData: VariantWithCostMetadataCount,
    ): string {
        const colName = columnConfig.columnName as string;

        if (colName === "costMetadataCount") {
            const count = rowData.costMetadata?.length ?? 0;
            return count > 0 ? `${count} assigned` : "None";
        }

        if (colName === "price") {
            return rowData.price ? `$${rowData.price}` : "";
        }

        if (colName === "compareAtPrice") {
            return rowData.compareAtPrice ? `$${rowData.compareAtPrice}` : "";
        }

        return super.resolveCellData(columnConfig, rowData);
    }
}
